import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { getToken } from '@/integrations/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Mail, Lock, ShieldCheck } from 'lucide-react';

// Phase A (SSO) — branded central login. Reached when a client app (HRMS/ITSM)
// redirects an unauthenticated user to /sso/login?client_id=&redirect_uri=&state=.
// After authenticating (or immediately, if already logged in centrally) it
// POSTs /api/sso/authorize and forwards the browser to the client's
// redirect_uri with a one-time code.

const Logo = () => (
  <div className="flex items-center gap-2.5">
    <svg width="34" height="34" viewBox="0 0 64 64" fill="none"><rect width="64" height="64" rx="15" fill="#2563EB" /><rect x="12" y="42" width="8" height="16" rx="2" fill="white" opacity=".5" /><rect x="24" y="32" width="8" height="26" rx="2" fill="white" opacity=".7" /><rect x="36" y="22" width="8" height="36" rx="2" fill="white" /><rect x="48" y="12" width="8" height="46" rx="2" fill="#22C55E" /></svg>
    <span className="text-xl font-extrabold text-white">
      <span style={{ color: '#3b82f6' }}>Prodi</span>fy<span style={{ color: '#22c55e', fontSize: 13, verticalAlign: 'super' }}>360</span>
    </span>
  </div>
);

const CLIENT_NAMES: Record<string, string> = {
  'hrms-client': 'Prodify360 HRMS',
  'itsm-client': 'Prodify360 ITSM',
  'crm-client': 'CRM Pro 360',
};

export default function SsoLogin() {
  const [params] = useSearchParams();
  const clientId = params.get('client_id') || '';
  const redirectUri = params.get('redirect_uri') || '';
  const state = params.get('state') || '';
  const scope = params.get('scope') || '';
  const appName = CLIENT_NAMES[clientId] || 'the Prodify360 ecosystem';

  const { signIn } = useAuth();
  const { toast } = useToast();
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [continuing, setContinuing] = useState<boolean>(!!getToken()); // silent path if already logged in
  const attempted = useRef(false);

  // Drive the /authorize exchange and forward to the client.
  async function continueSso(): Promise<boolean> {
    const token = getToken();
    if (!token) return false;
    const res = await fetch('/api/sso/authorize', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include', // so the shared prodify_sso cookie gets set
      body: JSON.stringify({ client_id: clientId, redirect_uri: redirectUri, state, scope }),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.error || `authorize failed (${res.status})`);
    }
    const { redirect } = await res.json();
    window.location.href = redirect; // leave the SPA → back to the client app
    return true;
  }

  // If the user is already authenticated centrally, skip the form entirely.
  useEffect(() => {
    if (!continuing || attempted.current) return;
    attempted.current = true;
    if (!clientId || !redirectUri) { setContinuing(false); return; }
    continueSso().catch((err) => {
      setContinuing(false);
      toast({ title: 'Sign-in error', description: err.message, variant: 'destructive' });
    });
  }, [continuing]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientId || !redirectUri) {
      toast({ title: 'Invalid SSO request', description: 'Missing client_id or redirect_uri.', variant: 'destructive' });
      return;
    }
    if (!form.email || !form.password) {
      toast({ title: 'Missing details', description: 'Enter your email and password.', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const { error } = await signIn(form.email, form.password);
      if (error) { toast({ title: 'Login failed', description: error.message, variant: 'destructive' }); return; }
      setContinuing(true);
      const ok = await continueSso();
      if (!ok) throw new Error('Could not establish SSO session.');
    } catch (err: any) {
      setContinuing(false);
      toast({ title: 'Error', description: err.message || 'Login failed', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0b0f1a] p-6 text-white relative overflow-hidden">
      <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-blue-600/25 blur-3xl" />
      <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-emerald-500/20 blur-3xl" />
      <div className="relative z-10 w-full max-w-sm rounded-2xl border border-white/10 bg-white/5 p-8 backdrop-blur">
        <div className="mb-6"><Logo /></div>

        {continuing ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
            <p className="text-sm text-white/80">Signing you in to <span className="font-semibold text-white">{appName}</span>…</p>
          </div>
        ) : (
          <>
            <h1 className="text-lg font-bold">Sign in to continue</h1>
            <p className="mb-6 mt-1 text-sm text-white/60">You're signing in to <span className="font-semibold text-white">{appName}</span> with your Prodify360 account.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="sso-email" className="text-white/80">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
                  <Input id="sso-email" type="email" autoComplete="username" className="pl-9 bg-white/10 border-white/10 text-white placeholder:text-white/40"
                    value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" />
                </div>
              </div>
              <div>
                <Label htmlFor="sso-pass" className="text-white/80">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
                  <Input id="sso-pass" type="password" autoComplete="current-password" className="pl-9 bg-white/10 border-white/10 text-white placeholder:text-white/40"
                    value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" />
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-500">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue to {CLIENT_NAMES[clientId] ? appName.replace('Prodify360 ', '') : 'app'}</>}
              </Button>
            </form>
            <div className="mt-6 flex items-center justify-center gap-1.5 text-xs text-white/40">
              <ShieldCheck className="h-3.5 w-3.5" /> Secured by Prodify360 SSO
            </div>
          </>
        )}
      </div>
    </div>
  );
}
